import React from "react";
import { classNames } from "../../../helpers";
import { useToast } from "../../../provider/ToastProvider";
import axiosInstance from "../../../utils/axiosInstance";
import { getUrlForBookings, getUrlForClasses } from "../../../utils/confighelpers";
import { FcModal, Icon, inputClass } from "./ui";

/**
 * Add a member to one session (new design, centred pop-up over the session drawer). Same rules as the old AddClassParticipantForm:
 * the member must have a usable credit pass / subscription credit for this class; the booking is made
 * with POST bookings/addClassBooking using that pass (paymentMethod "classPass" + passVoucherCode).
 */

const classIdOf = (detail) => detail?.classId?._id || detail?.classId || "";

/** The member's first pass that can book this class (credits left, or unlimited subscription). */
export const matchedVoucher = (member, classId) => {
  const list = Array.isArray(member?.purchase_detail) ? member.purchase_detail : [];
  return (
    list.find((f) => {
      const hasCredits =
        (f.total_voucher_unused !== undefined && parseInt(f.total_voucher_unused, 10) > 0) ||
        (f.productType === "subscription" && f.unlimitedCredits === true);
      if (!hasCredits || !Array.isArray(f.classId)) return false;
      return f.classId.length === 0 || f.classId.indexOf(classId) !== -1;
    }) || null
  );
};

const creditsLabel = (v) => {
  if (!v) return "No credits";
  if (v.productType === "subscription" && v.unlimitedCredits === true) return "Unlimited";
  const n = parseInt(v.total_voucher_unused, 10) || 0;
  return `${n} ${n === 1 ? "credit" : "credits"}`;
};

export default function AddParticipantDrawer({ open, onClose, detail, bookedUserIds = [], onAdded = () => null }) {
  const { addSuccessToast } = useToast();
  const [members, setMembers] = React.useState([]);
  const [loading, setLoading] = React.useState(false);
  const [query, setQuery] = React.useState("");
  const [picked, setPicked] = React.useState(null);
  const [error, setError] = React.useState("");
  const [saving, setSaving] = React.useState(false);
  const classId = classIdOf(detail);

  React.useEffect(() => {
    if (!open || !detail) return;
    setQuery("");
    setPicked(null);
    setError("");
    setSaving(false);
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const res = await axiosInstance({
          method: "get",
          url: `${getUrlForClasses("")}getMyClubMembers?invite=used&getall=true&businessId=${detail.addedByUserId}${classId ? `&classId=${classId}` : ""}`,
        });
        const raw = res?.data?.data;
        if (!cancelled) setMembers(Array.isArray(raw) ? raw : Array.isArray(raw?.data) ? raw.data : []);
      } catch (e) {
        if (!cancelled) setMembers([]);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [open, detail, classId]);

  const q = query.trim().toLowerCase();
  const rows = members
    .map((m) => {
      const c = m.consumer_detail || {};
      const voucher = matchedVoucher(m, classId);
      const booked = bookedUserIds.indexOf(String(c.consumerId || c._id || "")) > -1;
      return { m, c, voucher, booked };
    })
    .filter(({ c }) => !q || [c.fullName, c.email, c.username, c.phoneNumber].filter(Boolean).some((x) => String(x).toLowerCase().includes(q)))
    .sort((a, b) => (b.voucher ? 1 : 0) - (a.voucher ? 1 : 0) || String(a.c.fullName || "").localeCompare(String(b.c.fullName || "")));

  const save = async () => {
    if (!picked) {
      setError("Pick a member to add.");
      return;
    }
    const voucher = matchedVoucher(picked, classId);
    if (!voucher) {
      setError("This member has no credits for this class. They need to buy a pass first.");
      return;
    }
    const c = picked.consumer_detail || {};
    setSaving(true);
    try {
      const res = await axiosInstance({
        method: "post",
        url: `${getUrlForBookings()}addClassBooking`,
        data: {
          scheduleClassId: detail?._id || detail?.instanceId || "",
          type: "add",
          isPackage: detail?.isPackage || false,
          isEvent: detail?.isEvent || false,
          selectedEventPricing: (detail?.eventPricingList || []).map((p) => ({ bookingQty: 1, ticketType: p.ticketType })),
          username: c.username || "",
          fullName: c.fullName || "",
          email: c.email || "",
          isPaid: true,
          paymentMethod: "classPass",
          paymentMode: "Online",
          paymentStatus: "Paid",
          passVoucherCode: voucher.passVoucherCode,
        },
      });
      if (res?.data?.statusCode === 200) {
        addSuccessToast(`${c.fullName || "Member"} added to this session`);
        onAdded();
        onClose();
      } else {
        setError(typeof res?.data?.message === "string" ? res.data.message : "Couldn't add this member.");
      }
    } catch (e) {
      setError("Couldn't reach the server. Please try again.");
    } finally {
      setSaving(false);
    }
  };

  const pickedName = picked?.consumer_detail?.fullName;
  const pickedVoucher = picked ? matchedVoucher(picked, classId) : null;

  return (
    <FcModal
      open={open}
      onClose={onClose}
      eyebrow="ADD PARTICIPANT"
      title="Add a member to this session"
      subtitle="They're booked with one credit from their credit pass or subscription."
      primaryLabel={pickedName ? `Add ${pickedName.split(" ")[0]}` : "Add member"}
      onPrimary={save}
      busy={saving}
      primaryDisabled={!picked}
      footerNote={pickedVoucher ? <span>Uses 1 credit from <span className="font-extrabold text-gray-900">{pickedVoucher.passName || "their pass"}</span></span> : null}
    >
      <div>
        <div className="sticky top-0 z-10 -mx-1 bg-white px-1 pb-1">
          <div className="relative">
            <span className="pointer-events-none absolute inset-y-0 left-4 flex items-center text-gray-400">
              <Icon className="h-5 w-5" d={<><circle cx="11" cy="11" r="7" /><path d="m20 20-3.5-3.5" /></>} />
            </span>
            <input
              type="search"
              aria-label="Search members"
              placeholder="Search by name, email or phone"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              className={classNames(inputClass(false), "pl-11")}
            />
          </div>
        </div>
        {error && <div className="mt-3 rounded-2xl border border-red-200 bg-red-50 px-4 py-3 text-sm font-semibold text-red-700" role="alert">{error}</div>}

        <div className="mt-3">
          {loading ? (
            <div className="flex flex-col gap-2">
              {[0, 1, 2].map((i) => <div key={i} className="h-16 animate-pulse rounded-2xl bg-gray-100" />)}
            </div>
          ) : rows.length === 0 ? (
            <p className="py-6 text-center text-sm text-gray-500">{members.length ? "No members match your search." : "No members yet."}</p>
          ) : (
            <ul className="flex flex-col gap-1.5" role="radiogroup" aria-label="Members">
              {rows.map(({ m, c, voucher, booked }) => {
                const active = picked && picked._id === m._id;
                const disabled = booked || !voucher;
                return (
                  <li key={m._id}>
                    <button
                      type="button"
                      role="radio"
                      aria-checked={!!active}
                      disabled={disabled}
                      onClick={() => {
                        setPicked(m);
                        setError("");
                      }}
                      className={classNames(
                        "flex w-full items-center gap-3 rounded-2xl border p-3 text-left transition-colors",
                        active ? "border-indigo-bz bg-[#FBF8FE] ring-2 ring-[#F3E9FF]" : "border-transparent hover:bg-[#FBF8FE]",
                        disabled ? "cursor-not-allowed opacity-50 hover:bg-transparent" : ""
                      )}
                    >
                      {c.profilePic ? (
                        <img src={c.profilePic} alt="" className="h-11 w-11 shrink-0 rounded-full object-cover" />
                      ) : (
                        <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full bg-[#F3E9FF] text-sm font-black text-indigo-bz">{(c.fullName || "?").slice(0, 1)}</span>
                      )}
                      <span className="min-w-0 flex-1">
                        <span className="block truncate text-sm font-extrabold text-gray-900">{c.fullName || "Member"}</span>
                        <span className="block truncate text-xs text-gray-500">{c.email || c.username || ""}</span>
                      </span>
                      <span
                        className={classNames(
                          "shrink-0 rounded-full px-2.5 py-1 text-xs font-extrabold",
                          booked ? "bg-gray-100 text-gray-600" : voucher ? "bg-[#E8F7EE] text-[#2F6B0B]" : "bg-[#FFF4E5] text-[#7A4A00]"
                        )}
                      >
                        {booked ? "Booked" : creditsLabel(voucher)}
                      </span>
                    </button>
                  </li>
                );
              })}
            </ul>
          )}
        </div>
      </div>
    </FcModal>
  );
}
